import { motion } from "framer-motion";
import { Check } from "lucide-react";
import { cn } from "@/lib/utils";

/**
 * OptionCards — single-choice cards for the conversational wizard. One decision
 * per screen: each option is a card with optional icon + short description.
 * Selecting persists immediately (the wizard may auto-advance right after).
 */
export default function OptionCards({ options = [], value, onChange, columns = 2 }) {
  if (options.length === 0) {
    return (
      <p className="text-sm text-muted-foreground">
        Nenhuma opção disponível para esta pergunta.
      </p>
    );
  }

  return (
    <div
      className={cn(
        "grid gap-3",
        columns === 1 ? "sm:grid-cols-1" : columns === 3 ? "sm:grid-cols-3" : "sm:grid-cols-2",
      )}
      role="radiogroup"
    >
      {options.map((opt) => {
        const Icon = opt.icon;
        const active = value === opt.id;
        return (
          <motion.button
            key={opt.id}
            type="button"
            role="radio"
            aria-checked={active}
            onClick={() => onChange(opt.id)}
            whileTap={{ scale: 0.99 }}
            className={cn(
              "relative flex items-start gap-3 rounded-2xl border p-4 text-left transition-colors",
              active
                ? "border-cyan-500/70 bg-cyan-500/[0.06] ring-2 ring-cyan-500/20"
                : "border-border bg-surface/40 hover:border-border-strong",
            )}
          >
            {active && (
              <span className="absolute right-3 top-3 flex h-5 w-5 items-center justify-center rounded-full bg-cyan-500 text-white">
                <Check className="h-3.5 w-3.5" strokeWidth={3} />
              </span>
            )}
            {opt.iconSrc && (
              <img
                src={opt.iconSrc}
                alt=""
                className="mt-0.5 h-5 w-5 shrink-0 object-contain"
              />
            )}
            {!opt.iconSrc && Icon && (
              <Icon
                className={cn(
                  "mt-0.5 h-5 w-5 shrink-0",
                  active ? "text-cyan-600 dark:text-cyan-400" : "text-muted-foreground",
                )}
              />
            )}
            <span className="space-y-0.5 pr-6">
              <span className="block text-sm font-medium text-foreground">{opt.label}</span>
              {opt.desc && (
                <span className="block text-xs text-muted-foreground">{opt.desc}</span>
              )}
            </span>
          </motion.button>
        );
      })}
    </div>
  );
}
